import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import React, { useEffect, useCallback, useState } from 'react';
import { useGetOrderQuery } from '../../apis/mlkApi/mlk.api.hooks';
import { useOrderId } from '../../hooks/useOrderId';
import { Order } from '../../models/home.models';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Assistence, Back, Hamburger, MilkmanLogo, TimesIcon } from '../Icons';
import { SideMenu } from '../SideMenu/SideMenu';
import { PromoElement } from './components/PromoElement/PromoElement';
import './Layout.css';
import { PromoCookieElement } from './components/PromoElement/PromoCookieElement';
import 'swiper/css';
import { useGetCmsBannersQuery } from '../../apis/cmsApi/cms.hooks.api';
import { useLanguage } from '../../locale/useLanguage';
import { PromoBanner, PromoCookie } from '../../models';
import { getFromLocalStorage, StorageKey } from '../../helpers/localstorage';
import { ErrorModal } from './components/ErrorModal/ErrorModal';

import logo from '../../assets/images/logo.svg';
import { useIsNoAssign } from '../../hooks/useIsNoAssign';

export type OrderRequestResult = {
  data?: Order;
  isLoading: boolean;
  isFetching: boolean;
  error?: FetchBaseQueryError | SerializedError | undefined;
};

export type LayoutProps = Omit<BaseLayoutProps, 'isLoading'> & {
  onOrderChange?: (orderResult: OrderRequestResult) => void;
  orderIdOptional?: boolean;
};

export const Layout: React.FC<LayoutProps> = (props) => {
  const { onOrderChange, orderIdOptional = false, ...otherProps } = props;
  const orderId = useOrderId(orderIdOptional);
  const isNoAssign = useIsNoAssign();

  const orderResults = useGetOrderQuery(
    { orderId: orderId || '', isNoAssign },
    {
      skip: orderId === null,
    }
  );

  const promoCookies = orderResults.data?.promos || [];


  useEffect(() => {
    onOrderChange?.(orderResults);
  }, [onOrderChange, orderResults]);

  return (
    <BaseLayout
      {...otherProps}
      promoCookies={promoCookies}
      isLoading={orderResults.isLoading}
      orderId={orderId}
    />
  );
};

export type BaseLayoutProps = {
  hasViewportHeight?: boolean;
  hasMenu?: boolean;
  hasBack?: boolean;
  hasHelp?: boolean;
  hasInfo?: boolean;
  hasClose?: boolean;
  isHome?: boolean;
  title?: string;
  orderId?: string | null;
  isGreyBg?: boolean;
  isLoading?: boolean;
  promoCookies?: Array<PromoCookie>;
  onCloseClick?: () => void;
  onBackClick?: () => void;
  onHelpClick?: () => void;
  onInfoClick?: () => void;
  onMenuClick?: () => void;
};

export const BaseLayout: React.FC<BaseLayoutProps> = ({
  children,
  hasBack,
  hasHelp,
  hasInfo,
  hasClose,
  hasMenu,
  isHome,
  isGreyBg,
  isLoading = false,
  title,
  onBackClick,
  onHelpClick,
  onCloseClick,
  onInfoClick,
  hasViewportHeight,
  promoCookies,
  orderId,
}) => {
  const locale = useLanguage()[2];

  const viewportMaxHeightClass = hasViewportHeight
    ? 'layout--viewport-max-height'
    : '';
  const homeWrapperClass = isHome ? 'layout--wrapper-home' : '';
  const homeBodyClass = isHome ? 'layout--body-home' : '';
  const greyBgClass = isGreyBg ? 'layout--grey-bg' : '';

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [bannersToShow, setBannerToShow] = useState<PromoBanner[]>([]);
  const [cookiesToShow, setCookieToShow] = useState<PromoCookie[]>([]);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const openMenu = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const { data: promoBanners } = useGetCmsBannersQuery(
    { locale },
    { skip: !isHome }
  );

  const calcVisibleBanners = useCallback(() => {
    if (promoBanners) {
      const removedPromoIds: number[] = getFromLocalStorage(
        StorageKey.RemovedPromoBanners
      );

      let filteredBanners: PromoBanner[] = promoBanners;
      if (removedPromoIds) {
        filteredBanners = promoBanners.filter(
          (pb) => !removedPromoIds.includes(pb.id)
        );
      }
      setBannerToShow(filteredBanners);
    }
    if (promoCookies) {
      const removedCookieIds: string[] = getFromLocalStorage(
        StorageKey.RemovedPromoCookies
      );
      let filteredCookies: PromoCookie[] = promoCookies || [];
      if (removedCookieIds && orderId) {
        filteredCookies = (promoCookies || []).filter(
          (pc) => !removedCookieIds.includes(`${pc.id}:${orderId}`)
        );
      }
      setCookieToShow(filteredCookies);
    }
  }, [promoBanners, promoCookies, orderId]);

  useEffect(() => {
    calcVisibleBanners();
  }, [calcVisibleBanners]);

  return (
    <>
      <ErrorModal />
      <div
        className={`layout--wrapper ${viewportMaxHeightClass} ${greyBgClass} ${homeWrapperClass}`}
      >
        <header className="layout--header">
          <div className="layout--navbar">
            {hasBack && (
              <a
                role="link"
                aria-label="Back"
                onClick={onBackClick}
                className="layout--navbar-icon"
              >
                <Back width="24px" className="layout--icon-color" />
              </a>
            )}
            {(isHome || hasMenu) && (
              <a
                role="menu"
                aria-label="Menu"
                onClick={openMenu}
                className="layout--navbar-icon"
              >
                <Hamburger
                  className="layout--icon-color"
                  width="24px"
                  height={24}
                />
              </a>
            )}
            {!isHome && !hasMenu && !hasBack && (
              <span className="layout--placeholder"></span>
            )}
            {title ? (
              <h1 className="layout--title">{title}</h1>
            ) : (
              <img className="layout--logo" src={logo} alt="Logo" />
            )}
            {hasHelp && (
              <a
                role="link"
                aria-label="Help"
                onClick={onHelpClick}
                className="layout--navbar-icon"
              >
                <Assistence
                  width="24px"
                  height="24px"
                  className="layout--icon-color"
                />
              </a>
            )}
            {hasInfo && (
              <a
                role="link"
                aria-label="Info"
                onClick={onInfoClick}
                className="layout--navbar-icon"
              >
                <Assistence
                  width="24px"
                  height="24px"
                  className="layout--icon-color"
                />
              </a>
            )}
            {hasClose && (
              <a
                role="link"
                aria-label="Close"
                onClick={onCloseClick}
                className="layout--navbar-icon"
              >
                <TimesIcon
                  width="24px"
                  height="24px"
                  className="layout--icon-color"
                />
              </a>
            )}
            {!hasClose && !hasHelp && !hasInfo && (
              <span className="layout--placeholder"></span>
            )}
          </div>
          {isHome && (bannersToShow || cookiesToShow) && (
            <div className="layout--promotions-container">
              <Swiper
                slidesPerView={'auto'}
                centeredSlides
                grabCursor
                spaceBetween={16}
                slideToClickedSlide
                className="layout--promo-swiper"
              >
                {cookiesToShow.length > 0 &&
                  cookiesToShow.map((pc) => (
                    <SwiperSlide key={pc.id}>
                      <PromoCookieElement
                        onBannerRemoved={calcVisibleBanners}
                        promoCookie={pc}
                        orderId={orderId}
                      />
                    </SwiperSlide>
                  ))}
                {bannersToShow.length > 0 &&
                  bannersToShow.map((pb) => (
                    <SwiperSlide key={pb.id}>
                      <PromoElement
                        onBannerRemoved={calcVisibleBanners}
                        promoBanner={pb}
                      />
                    </SwiperSlide>
                  ))}
              </Swiper>
            </div>
          )}
        </header>
        <div className={`layout--body ${homeBodyClass}`}>
          {isLoading ? null : children}
        </div>
        {isHome && (
          <footer className="layout--footer">
            Powered by{' '}
            <MilkmanLogo
              data-testid="Layout--MilkmanLogo"
              role="contentinfo"
              aria-label="Milkman"
              className="layout--footer-logo"
            />
          </footer>
        )}
      </div>
      <SideMenu isMenuOpen={isMenuOpen} onCloseMenu={closeMenu} />
    </>
  );
};

export default Layout;
